import type { AxiosRequestConfig } from "axios";
import { axiosInstance } from "@/lib/axios";
import type {
  AuctionItem,
  AuctionPayload,
  AuctionsPageMeta,
  AuctionsQueryParams,
  AuctionsResponse,
  SingleAuctionResponse,
} from "./types";

const EMPTY_PAGE: AuctionsPageMeta = {
  current_page: 1,
  last_page: 1,
  per_page: 0,
  total: 0,
};

function normalizeModelData(raw: any): AuctionItem["model_data"] {
  const data = raw?.model_type?.data ?? raw?.model_data ?? null;
  if (!data || typeof data !== "object") return null;
  return {
    id: Number(data?.id ?? 0),
    name: data?.name ?? null,
    description: data?.description ?? null,
    image: data?.image ?? null,
    contract_address: data?.contract_address ?? null,
    collection_id: data?.collection_id != null ? Number(data.collection_id) : null,
    token_id: data?.token_id != null ? Number(data.token_id) : null,
    network_name: data?.network_name ?? null,
    royalty: data?.royalty != null ? Number(data.royalty) : null,
    quantity: data?.quantity != null ? Number(data.quantity) : null,
    standard: data?.standard != null ? Number(data.standard) : null,
    ipfs_address: data?.ipfs_address ?? null,
    source: data?.source ?? null,
    status: data?.status ?? null,
    created_at: data?.created_at ?? null,
  };
}

function resolveModelType(raw: any): AuctionItem["model_type"] {
  // model_type can be a string ("nft"/"collection") or an object ({ type: "NFT", ... }).
  const rawValue =
    typeof raw?.model_type === "object" && raw?.model_type !== null
      ? raw?.model_type?.type
      : raw?.model_type;
  const value = String(rawValue ?? "").toLowerCase();
  // Legacy auctions may still carry "nftcollection" — treat it as "collection"
  if (value === "nftcollection" || value === "collection") return "collection";
  return "nft";
}

export function normalizeAuctionItem(raw: any): AuctionItem {
  const modelData = normalizeModelData(raw);
  return {
    id: Number(raw?.id ?? 0),
    title: String(raw?.title ?? ""),
    slug: String(raw?.slug ?? ""),
    image: raw?.image ?? modelData?.image ?? raw?.model?.image ?? raw?.model?.logo ?? null,
    start_price: Number(raw?.start_price ?? 0),
    min_price: Number(raw?.min_price ?? 0),
    max_price: Number(raw?.max_price ?? 0),
    start_time: String(raw?.start_time ?? raw?.created_at ?? ""),
    expire_time: String(raw?.expire_time ?? ""),
    currency_id: Number(raw?.currency_id ?? raw?.currency?.id ?? 0),
    network_id: Number(raw?.network_id ?? raw?.network?.id ?? 0),
    max_bids_per_user: Number(raw?.max_bids_per_user ?? 0),
    my_bids_count: raw?.my_bids_count == null || !Number.isFinite(Number(raw.my_bids_count)) ? null : Number(raw.my_bids_count),
    model_id: Number(raw?.model_id ?? raw?.model?.id ?? raw?.model_type?.id ?? modelData?.id ?? 0),
    model_type: resolveModelType(raw),
    status: raw?.status ?? "draft",
    is_published: Boolean(raw?.is_published),
    is_automatic_accept: Boolean(raw?.is_automatic_accept),
    user_id: raw?.user_id != null ? Number(raw.user_id) : null,
    created_at: raw?.created_at,
    updated_at: raw?.updated_at,
    currency: raw?.currency ?? null,
    network: raw?.network ?? null,
    user: raw?.user ?? raw?.creator ?? null,
    model: raw?.model ?? null,
    creator: raw?.creator ?? null,
    model_data: modelData,
    likes: raw?.likes ?? undefined,
    current_user: raw?.current_user ?? undefined,
    // Kept so a list can say how many bids an auction has (the v2 listing); the single page reads its own bids endpoint.
    bids: Array.isArray(raw?.bids) ? raw.bids : undefined,
  };
}

function normalizePageMeta(raw: any): AuctionsPageMeta {
  const page = raw?.page ?? raw?.pagination ?? raw ?? {};
  return {
    current_page: Number(page?.current_page ?? 1),
    last_page: Number(page?.last_page ?? 1),
    per_page: Number(page?.per_page ?? 0),
    total: Number(page?.total ?? 0),
  };
}

export async function getAuctions(params: AuctionsQueryParams = {}): Promise<AuctionsResponse> {
  const endpoint = params.watchlist ? "/auctions/watchlist" : "/auctions";

  const search = new URLSearchParams();
  if (params.user_id) search.set("user_id", String(params.user_id));
  if (params.title) search.set("title", params.title);
  if (params.model_type) search.set("model_type", params.model_type);
  if (params.model_id) search.set("model_id", String(params.model_id));
  if (params.currency_id) search.set("currency_id", String(params.currency_id));
  if (params.limit) search.set("limit", String(params.limit));
  if (params.page) search.set("page", String(params.page));
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);

  const { data } = await axiosInstance.get(`${endpoint}${search.toString() ? `?${search.toString()}` : ""}`, {
    headers: params.lang ? { "lang": params.lang } : undefined,
  });

  const rawItems =
    data?.data?.items ??
    data?.data?.auctions ??
    data?.data ??
    [];

  return {
    data: {
      items: Array.isArray(rawItems) ? rawItems.map(normalizeAuctionItem) : [],
      page: normalizePageMeta(data?.data?.page ?? data?.meta ?? data?.data),
    },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export async function createAuction(payload: AuctionPayload, config?: AxiosRequestConfig): Promise<SingleAuctionResponse> {
  const { data } = await axiosInstance.post("/auctions", payload, config);
  const rawItem =
    data?.data?.item ??
    data?.data?.auction ??
    data?.data;

  return {
    data: {
      item: normalizeAuctionItem(rawItem),
    },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export async function updateAuction(auctionId: number | string, payload: AuctionPayload, config?: AxiosRequestConfig): Promise<SingleAuctionResponse> {
  const { data } = await axiosInstance.post(`/auctions/update/${auctionId}`, payload, config);
  const rawItem =
    data?.data?.item ??
    data?.data?.auction ??
    data?.data;

  return {
    data: {
      item: normalizeAuctionItem(rawItem),
    },
    message: data?.message ?? null,
    next: data?.next ?? null,
    errors: data?.errors ?? null,
    meta: data?.meta ?? [],
  };
}

export async function deleteAuction(auctionId: number | string): Promise<any> {
  const { data } = await axiosInstance.delete(`/auctions/delete/${auctionId}`);
  return data;
}

export async function cancelAuction(auctionId: number | string): Promise<any> {
  const { data } = await axiosInstance.post(`/auctions/${auctionId}/cancel`);
  return data;
}

export function getEmptyAuctionsResponse(): AuctionsResponse {
  return {
    data: {
      items: [],
      page: EMPTY_PAGE,
    },
    message: null,
    next: null,
    errors: null,
    meta: [],
  };
}
